import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ago } from '../../components/CalendarFeeds';
import { useSettings } from './SettingsContext';
import Icon from './icons';
import { findPage, pathOf, privacyRows, settingOfKey } from './registry';

const API = process.env.REACT_APP_API_URL;

/* ================================================================ OVERVIEW */

/**
 * Settings -> Firm & system -> Overview, where /settings opens.
 *
 * SETUP STATUS. Every "run migrate:0xx" notice the pages already detect, in
 * one place: Jarvis deploys before its migrations run, and each page used to
 * say so only to whoever happened to open it. Each check asks the same
 * question that page asks - a 503, a `ready: false`, a missing field - and
 * links to the page that explains it. Answered "set up" when a check cannot
 * tell, so the list never invents work.
 *
 * LAST CHANGES. The latest value of each firm rule somebody saved (GET
 * /app-settings/recent - app_settings keeps who saved each one and when,
 * not a history) and the feature switches' own history, newest first.
 */
const status503 = (path) => axios.get(`${API}${path}`)
  .then(() => true).catch((e) => (e?.response?.status === 503 ? false : true));

function useSetupChecks() {
  const { rules, features } = useSettings();
  const [probe, setProbe] = useState(null);
  useEffect(() => {
    let live = true;
    Promise.all([
      status503('/asset-admin/categories'),
      status503('/asset-admin/companies'),
      status503('/asset-admin/enrolment-requests'),
      status503('/docs/admin/overview'),
      status503('/calendar-feeds'),
      // 127 (AI in tickets and docs): the usage card's own answer says.
      axios.get(`${API}/ai/usage`).then((r) => r.data?.ready !== false).catch(() => true),
      axios.get(`${API}/auth/admins`).then((r) => !(r.data || []).some((a) => a.approver_flag_available === false)).catch(() => true)
    ]).then(([register, companies, enrol, docs, feeds, ai, approverFlag]) => {
      if (live) setProbe({ register, companies, enrol, docs, feeds, ai, approverFlag });
    });
    return () => { live = false; };
  }, []);

  return useMemo(() => {
    if (!probe || !rules || !features) return null;
    const setup = rules.setup || {};
    const at = (g, p, opts) => { const page = findPage(g, p); return page ? pathOf(page, opts) : '/settings'; };
    const rows = [
      { id: '016', what: 'Admins who do not approve', ok: probe.approverFlag, where: at('people', 'users', { anchor: 'access' }) },
      { id: '039', what: 'The asset register', ok: probe.register, where: at('assets', 'register') },
      { id: '062', what: 'Companies in the register', ok: !probe.register || probe.companies, where: at('assets', 'companies') },
      { id: '069', what: 'Self-installs waiting for approval', ok: !probe.register || probe.enrol, where: at('assets', 'enrolment', { anchor: 'waiting' }) },
      { id: '077', what: 'Event calendars on the to-do Calendar', ok: probe.feeds, where: at('content', 'calendars') },
      { id: '078', what: 'Feature access (who can use what)', ok: features.failed ? true : features.ready !== false, where: at('people', 'feature-access') },
      { id: '079', what: 'Contractors creating groups, groups by rule', ok: rules.chat_group_rules_ready !== false, where: at('communication', 'groups') },
      { id: '080', what: 'Profile photos', ok: setup.m080_profile_photos !== false, where: at('people', 'profile-photos') },
      { id: '083', what: 'Documentation', ok: probe.docs, where: at('content', 'documentation') },
      { id: '092', what: 'Discovery: devices without a heartbeat, known networks, floor plans', ok: setup.m092_discovery !== false, where: at('assets', 'enrolment', { anchor: 'discovery' }) },
      { id: '086', what: 'Private notes on tickets', ok: setup.m086_private_notes !== false, where: at('tickets', 'ticket-rules', { anchor: 'private-notes' }) },
      { id: '087', what: 'Approvals sent back to the requester', ok: setup.m087_sent_back !== false, where: at('tickets', 'ticket-rules', { anchor: 'approver' }) },
      {
        id: '088', what: 'One switch for recording; contractors managing ticket templates',
        ok: setup.m088_settings !== false && rules.recording_legacy_off !== true,
        where: at('communication', 'recording', { anchor: 'feature-recording' })
      },
      { id: '089', what: 'The Private to-do list; who sees the team workload; files on to-dos', ok: setup.m089_todo_private !== false, where: at('content', 'calendars', { anchor: 'todo-workload' }) },
      { id: '090', what: 'Start meeting now; recording a call held on another app', ok: setup.m090_meetings_adhoc !== false, where: at('communication', 'recording', { anchor: 'feature-recording' }) },
      { id: '091', what: 'Purchase and Subscription as ticket categories', ok: setup.m091_request_categories !== false, where: at('tickets', 'categories') },
      { id: '095', what: 'Discovery: where a thing was seen, why it was ignored, personal marks, the clean-up of devices linked automatically', ok: setup.m095_discovery_noise !== false, where: at('assets', 'device-agent', { anchor: 'attached-autolink-office-only' }) },
      { id: '097', what: 'Email to to-do (jarvis@todo.ncmb.ai); private calendar links', ok: setup.m097_todo_email_calendar !== false, where: at('content', 'calendars', { anchor: 'todo-email' }) },
      { id: '106', what: 'Emails wait to be sorted into to-dos, with AI suggestions', ok: setup.m106_todo_email_sort !== false, where: at('content', 'calendars', { anchor: 'todo-email-arrives' }) },
      { id: '109', what: 'The server error log and background job history; refused (unverified) emails', ok: setup.m109_ops_log !== false, where: at('system', 'overview', { anchor: 'errors' }) },
      { id: '110', what: 'The migrations ledger: every database update still to run, listed here from the server itself', ok: setup.m110_schema_migrations !== false, where: at('system', 'overview', { anchor: 'setup' }) },
      { id: '127', what: 'AI in tickets and docs: the AI usage log and ticket summaries', ok: probe.ai, where: at('communication', 'recording', { anchor: 'feature-ai_tickets' }) }
    ];
    /*
     * SINCE 110 THE SERVER KNOWS. GET /app-settings carries `migrations`
     * (administrators): every file in backend/migrations the database has
     * not had, from the schema_migrations ledger. Each pending file becomes a
     * row here, so a migration nobody wrote a hand-made row for is still
     * listed. The rows above stay as the fallback before 110 and as the
     * link to the page each one changes.
     */
    const ledger = rules.migrations;
    if (ledger && ledger.ready && Array.isArray(ledger.pending)) {
      const known = new Set(rows.map((c) => c.id));
      for (const id of ledger.pending) {
        const num = /^(\d+)_/.exec(id)?.[1] || id;
        if (known.has(num)) {
          // The hand-made row knows the page; the ledger knows it is pending.
          const row = rows.find((c) => c.id === num);
          row.ok = false;
          row.command = 'npm run migrate';
          row.ledger = true;
          continue;
        }
        rows.push({ id: num, what: id, ok: false, where: at('system', 'overview', { anchor: 'setup' }), command: 'npm run migrate', ledger: true, file: id });
      }
    }
    return rows;
  }, [probe, rules, features]);
}

function CheckList({ checks }) {
  return (
    <ul className="stn-checks">
      {checks.map((c) => (
        <li key={c.id} className={`stn-check${c.ok ? ' is-ok' : ' is-needed'}`} data-migration={c.id} data-ledger={c.ledger ? 'pending' : undefined}>
          <Icon name={c.ok ? 'check' : 'warn'} className="stn-check-icon" />
          <span className="stn-check-what">
            <Link to={c.where}>{c.what}</Link>
            <small>Migration {c.id}{c.ledger ? ' · pending in the server’s ledger' : ''}</small>
          </span>
          <span className="stn-check-state">
            {c.ok ? 'Set up' : <>Run <code>{c.command || `npm run migrate:${c.id}`}</code></>}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------- the ops log */

/**
 * The two cards migration 109 adds (routes/ops.js): the last day of server
 * errors, client crashes and degraded answers, and every background job with
 * its last run and when it is next due. Administrators only - the page is
 * theirs anyway - and before the migration each card says to run it.
 */
const OPS_WORDS = { server: 'Server', client: 'Browser' };
const codeWord = (e) => {
  if (!e.code) return e.kind === 'client' ? 'crash' : 'error';
  if (e.code.startsWith('degraded:')) return `degraded (${e.code.slice('degraded:'.length)})`;
  return e.code;
};
const everyWord = (ms) => {
  if (!ms) return '';
  const m = Math.round(ms / 60000);
  if (m < 60) return `every ${m} min`;
  const h = Math.round(m / 60);
  return h < 24 ? `every ${h} h` : 'daily';
};
const dueWord = (iso) => {
  if (!iso) return 'not on this instance';
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return 'now';
  const m = Math.round(ms / 60000);
  if (m < 1) return 'within a minute';
  if (m < 60) return `in ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `in ${h} h`;
  const d = Math.round(h / 24);
  return `in ${d} day${d === 1 ? '' : 's'}`;
};

function useOps(me) {
  const [errors, setErrors] = useState(null);   // { total, errors } | { notReady } | { failed }
  const [jobs, setJobs] = useState(null);       // { jobs } | { notReady } | { failed }
  const admin = Boolean(me && me.isAdmin);
  const load = useCallback(() => {
    if (!admin) return;
    const fold = (set) => (err) => set(err?.response?.status === 503 ? { notReady: true } : { failed: true });
    axios.get(`${API}/ops/errors`, { params: { hours: 24, limit: 50 } })
      .then((r) => setErrors({ total: Number(r.data?.total) || 0, errors: Array.isArray(r.data?.errors) ? r.data.errors : [] }))
      .catch(fold(setErrors));
    axios.get(`${API}/ops/jobs`)
      .then((r) => setJobs({ jobs: Array.isArray(r.data?.jobs) ? r.data.jobs : [], instance: r.data?.instance || null }))
      .catch(fold(setJobs));
    axios.get(`${API}/ops/backups`)
      .then((r) => setBackups({ info: r.data || {} }))
      .catch(fold(setBackups));
  }, [admin]);
  const [backups, setBackups] = useState(null); // { info } | { notReady } | { failed }
  useEffect(() => { load(); }, [load]);
  return { errors, jobs, backups, reload: load, admin };
}

/**
 * The Backups card: the daily pg_dump to R2 (lib/backups.js, the db-backup
 * job) - on or off, whether this server can take one at all (pg_dump in the
 * image, R2 configured), and the newest run with the dump's name and size.
 * Railway keeps its own backups of the database service too; the card says
 * where, because that is the copy to reach for first.
 */
const mb = (s) => { const m = /\((\d+(?:\.\d+)?) MB/.exec(s || ''); return m ? `${m[1]} MB` : null; };
function BackupsCard({ backups, reload }) {
  const info = backups && backups.info;
  const last = info && info.last;
  const state = !info ? null
    : !info.enabled ? ['off', 'is-quiet', 'Daily backup off']
      : !info.pg_dump?.ok ? ['blocked', 'is-bad', 'Cannot run here']
        : !last ? ['waiting', 'is-wait', 'No backup yet']
          : last.finished_at === null ? ['running', 'is-wait', 'Running']
            : last.ok ? ['ok', 'is-good', 'Last backup ok'] : ['failed', 'is-bad', 'Last backup failed'];
  return (
    <section className="stn-card" id="s-backups" aria-labelledby="stn-backups-h" data-ops-card="backups">
      <div className="stn-section-head">
        <h3 className="stn-card-title" id="stn-backups-h">Backups</h3>
        {state && <span className={`stn-pill ${state[1] === 'is-good' ? 'is-good' : state[1] === 'is-bad' ? 'is-warn' : ''}`} data-ops-backups={state[0]}>{state[2]}</span>}
        <button type="button" className="btn btn-secondary btn-small" onClick={reload} disabled={backups === null}>Refresh</button>
      </div>
      <p className="stn-help">
        Once a day at {info ? `${String(info.hour).padStart(2, '0')}:00` : '02:00'} (Malta time) the server dumps the whole database with pg_dump and keeps the
        newest {info ? info.keep : 30} copies in the firm’s R2 bucket. docs/OPERATIONS.md has the restore drill.
      </p>
      {backups === null && <p className="stn-quiet">Loading…</p>}
      {backups && backups.notReady && <NotReady />}
      {backups && backups.failed && <p className="stn-row-problem">Could not read the backup state.</p>}
      {info && (
        <dl className="stn-ops-facts" data-ops-backups-facts="">
          <div><dt>Daily job</dt><dd>{info.enabled ? 'on (BACKUP_ENABLED=1)' : 'off - set BACKUP_ENABLED=1 on the backend service to turn it on'}</dd></div>
          <div><dt>pg_dump on this server</dt><dd>{info.pg_dump?.ok ? `yes, ${info.pg_dump.version}` : <span className="stn-ops-error">{info.pg_dump?.error || 'no'}</span>}</dd></div>
          <div><dt>R2 bucket</dt><dd>{info.r2?.configured ? info.r2.bucket : 'not configured - the dump stays on the server’s disk, which Railway discards'}</dd></div>
          <div>
            <dt>Last backup</dt>
            <dd>
              {!last ? <span className="stn-quiet">none recorded</span> : (
                <>
                  <time dateTime={last.started_at} title={new Date(last.started_at).toLocaleString('en-GB')}>{ago(last.started_at)}</time>
                  {' · '}
                  {last.finished_at === null ? 'running' : last.ok ? `ok${mb(last.detail) ? `, ${mb(last.detail)}` : ''}` : 'failed'}
                  {last.detail && <small className="stn-quiet"> {last.detail}</small>}
                  {last.error && <small className="stn-ops-error"> {last.error}</small>}
                </>
              )}
            </dd>
          </div>
        </dl>
      )}
      <p className="stn-quiet" data-ops-railway="">
        Railway’s own backups of the database service: see {info && info.railway_url
          ? <a href={info.railway_url} target="_blank" rel="noreferrer">the Railway dashboard</a>
          : 'the Railway dashboard'} (the Postgres service → Backups). They are the first copy to reach for.
      </p>
    </section>
  );
}

function NotReady() {
  return (
    <p className="stn-banner is-info" data-ops-notready="" role="note">
      Waiting for a database update on the server: run <code>npm run migrate:109</code>. Until then nothing is recorded here.
    </p>
  );
}

/**
 * "AI this month" (wave 3 batch 5), administrators only: every call Jarvis
 * made to Claude this month, per feature - how many, how many failed, and
 * an ESTIMATED cost from the price table in backend/lib/ai.js. The bill is
 * Anthropic's console; this is for seeing which feature costs what. Never
 * what was sent: the log has no text in it.
 */
const usd = (cents) => `$${(Number(cents || 0) / 100).toFixed(2)}`;
const MONTH = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'Europe/Malta' });
function AiUsageCard({ me }) {
  const admin = Boolean(me && me.isAdmin);
  const [u, setU] = useState(null);            // the answer | { failed }
  useEffect(() => {
    if (!admin) return undefined;
    let live = true;
    axios.get(`${API}/ai/usage`).then((r) => { if (live) setU(r.data || { failed: true }); }).catch(() => { if (live) setU({ failed: true }); });
    return () => { live = false; };
  }, [admin]);
  if (!admin) return null;
  const month = u && u.month ? MONTH.format(new Date(`${u.month}-15T12:00:00Z`)) : 'this month';
  return (
    <section className="stn-card" id="s-ai" aria-labelledby="stn-ai-h" data-ops-card="ai">
      <div className="stn-section-head">
        <h3 className="stn-card-title" id="stn-ai-h">AI this month</h3>
        {u && u.total && (
          <span className="stn-pill is-good" data-ai-total={u.total.calls}>{u.total.calls} call{u.total.calls === 1 ? '' : 's'} · about {usd(u.total.cost_cents)}</span>
        )}
      </div>
      <p className="stn-help">
        Every request Jarvis made to Claude in {month}, by feature, with an estimated cost in US dollars from
        Anthropic’s list prices - an estimate, not the bill (that is in Anthropic’s console). What was sent and
        what came back are never kept.{u && u.configured === false ? ' This server has no ANTHROPIC_API_KEY, so nothing can be sent.' : ''}
      </p>
      {u === null && <p className="stn-quiet">Loading…</p>}
      {u && u.failed && <p className="stn-row-problem">Could not read the AI usage.</p>}
      {u && u.ready === false && (
        <p className="stn-banner is-info" role="note" data-ai-notready="">
          Waiting for a database update on the server: run <code>npm run migrate:127</code>. Until then nothing is counted here.
        </p>
      )}
      {u && u.features && u.features.length === 0 && <p className="stn-quiet" data-ops-empty="ai">No requests to Claude yet this month.</p>}
      {u && u.features && u.features.length > 0 && (
        <div className="stn-ops-wrap">
          <table className="stn-ops" data-ops-table="ai">
            <thead>
              <tr><th scope="col">Feature</th><th scope="col">Calls</th><th scope="col">Failed</th><th scope="col">Estimated cost</th></tr>
            </thead>
            <tbody>
              {u.features.map((f) => (
                <tr key={f.feature} data-ai-feature={f.feature}>
                  <td>{f.label}</td>
                  <td>{f.calls}</td>
                  <td>{f.failed || '—'}</td>
                  <td>{usd(f.cost_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function OpsCards({ me }) {
  const { errors, jobs, backups, reload, admin } = useOps(me);
  if (!admin) return null;
  return (
    <>
      <BackupsCard backups={backups} reload={reload} />
      <section className="stn-card" id="s-errors" aria-labelledby="stn-errors-h" data-ops-card="errors">
        <div className="stn-section-head">
          <h3 className="stn-card-title" id="stn-errors-h">Recent server errors (last 24 h)</h3>
          {errors && errors.errors && (
            <span className={`stn-pill ${errors.total ? 'is-warn' : 'is-good'}`} data-ops-errors={errors.total}>
              {errors.total ? `${errors.total} in 24 h` : 'None in 24 h'}
            </span>
          )}
        </div>
        <p className="stn-help">
          Every request the server answered with a 5xx, every crash a browser reported, every answer that came back
          with less than it should, and every administrator entry bound to or refused for an Entra id. The request id
          is what to ask for when somebody reads an error out; it finds the lines in the server log. Kept thirty days.
        </p>
        {errors === null && <p className="stn-quiet">Loading…</p>}
        {errors && errors.notReady && <NotReady />}
        {errors && errors.failed && <p className="stn-row-problem">Could not read the error log.</p>}
        {errors && errors.errors && errors.errors.length === 0 && <p className="stn-quiet" data-ops-empty="errors">Nothing has gone wrong in the last day.</p>}
        {errors && errors.errors && errors.errors.length > 0 && (
          <div className="stn-ops-wrap">
            <table className="stn-ops" data-ops-table="errors">
              <thead>
                <tr><th scope="col">When</th><th scope="col">Where</th><th scope="col">What</th><th scope="col">Request</th></tr>
              </thead>
              <tbody>
                {errors.errors.map((e) => (
                  <tr key={e.id} data-ops-kind={e.kind} data-ops-code={e.code || ''}>
                    <td><time dateTime={e.at} title={new Date(e.at).toLocaleString('en-GB')}>{ago(e.at)}</time></td>
                    <td className="stn-ops-where">
                      <span className="stn-ops-kind">{OPS_WORDS[e.kind] || e.kind}</span>
                      {e.status ? <span className="stn-ops-status">{e.status}</span> : null}
                      <code>{e.route || '—'}</code>
                    </td>
                    <td className="stn-ops-what">
                      <span className="stn-ops-code">{codeWord(e)}</span>
                      <span className="stn-ops-message">{e.message}</span>
                      {e.actor_key && <small className="stn-quiet">{e.actor_key}</small>}
                    </td>
                    <td><code className="stn-ops-rid" title={e.request_id || ''}>{e.request_id ? e.request_id.slice(0, 8) : '—'}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="stn-card" id="s-jobs" aria-labelledby="stn-jobs-h" data-ops-card="jobs">
        <div className="stn-section-head">
          <h3 className="stn-card-title" id="stn-jobs-h">Background jobs</h3>
          <button type="button" className="btn btn-secondary btn-small" onClick={reload} disabled={jobs === null}>Refresh</button>
        </div>
        <p className="stn-help">
          The clocks Jarvis runs on its own - SLA and stall sweeps, transcriptions, the ECB rates, the maker registry,
          the event calendars, the email retry, the approval digest, retention. Each runs on one instance at a time and
          every run is recorded, so a sweep that has quietly stopped shows here as a last run that is too old.
        </p>
        {jobs === null && <p className="stn-quiet">Loading…</p>}
        {jobs && jobs.notReady && <NotReady />}
        {jobs && jobs.failed && <p className="stn-row-problem">Could not read the job history.</p>}
        {jobs && jobs.jobs && jobs.jobs.length === 0 && <p className="stn-quiet" data-ops-empty="jobs">No job has run yet.</p>}
        {jobs && jobs.jobs && jobs.jobs.length > 0 && (
          <div className="stn-ops-wrap">
            <table className="stn-ops" data-ops-table="jobs">
              <thead>
                <tr><th scope="col">Job</th><th scope="col">Last ran</th><th scope="col">Result</th><th scope="col">Next due</th></tr>
              </thead>
              <tbody>
                {jobs.jobs.map((j) => {
                  const result = j.last_started_at === null ? ['never run', 'is-quiet']
                    : j.last_finished_at === null ? ['running', 'is-wait']
                      : j.last_ok ? ['ok', 'is-good'] : ['failed', 'is-bad'];
                  return (
                    <tr key={j.job} data-ops-job={j.job} data-ops-result={result[0]}>
                      <td><code>{j.job}</code>{j.every_ms ? <small className="stn-quiet"> {everyWord(j.every_ms)}</small> : null}</td>
                      <td>{j.last_started_at ? <time dateTime={j.last_started_at} title={new Date(j.last_started_at).toLocaleString('en-GB')}>{ago(j.last_started_at)}</time> : <span className="stn-quiet">never</span>}</td>
                      <td>
                        <span className={`stn-ops-result ${result[1]}`}>{result[0]}</span>
                        {j.last_error && <small className="stn-ops-error">{j.last_error}</small>}
                      </td>
                      <td>{dueWord(j.next_due_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

const SHORTCUTS = [
  ['people', 'users'], ['people', 'contractors'], ['people', 'feature-access'],
  ['tickets', 'ticket-rules'], ['communication', 'recording'], ['assets', 'device-agent'],
  ['content', 'documentation'], ['system', 'privacy']
];

export function OverviewPage() {
  const ctx = useSettings();
  const checks = useSetupChecks();
  const [recent, setRecent] = useState(null);
  useEffect(() => {
    axios.get(`${API}/app-settings/recent`, { params: { limit: 10 } })
      .then((r) => setRecent(Array.isArray(r.data?.changes) ? r.data.changes : []))
      .catch(() => setRecent([]));
  }, [ctx.rules]);

  const changes = useMemo(() => {
    if (recent === null) return null;
    const rules = recent.map((c) => {
      const s = settingOfKey(c.key);
      let value = null;
      try { value = s?.setting.value ? s.setting.value(ctx) : null; } catch { value = null; }
      return {
        id: `r:${c.key}`, at: c.updated_at, by: c.updated_by,
        label: s ? s.setting.label : c.key, where: s ? `${s.page.groupLabel} › ${s.page.label}` : null,
        path: s ? s.path : null, value
      };
    });
    const feats = ((ctx.features && ctx.features.history) || []).map((h) => ({
      id: `f:${h.id}`, at: h.changed_at, by: h.changed_by, label: h.said,
      where: 'People & access › Feature access', path: pathOf(findPage('people', 'feature-access'), { anchor: `feature-${h.feature}` }), value: null
    }));
    return [...rules, ...feats].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 10);
  }, [recent, ctx]);

  const missing = (checks || []).filter((c) => !c.ok);
  return (
    <div className="stn-overview">
      <section className="stn-card" id="s-setup" aria-labelledby="stn-setup-h">
        <div className="stn-section-head">
          <h3 className="stn-card-title" id="stn-setup-h">Setup status</h3>
          {checks && (
            <span className={`stn-pill ${missing.length ? 'is-warn' : 'is-good'}`} data-setup={missing.length ? 'needed' : 'ok'}>
              {missing.length ? `${missing.length} to run` : 'All set up'}
            </span>
          )}
        </div>
        {!checks ? <p className="stn-quiet">Checking the server…</p> : (
          <>
            <p className="stn-help">
              {missing.length
                ? 'These parts of Jarvis are waiting for a database update on the server. Until each one runs, that part works as it did before it existed.'
                : 'Every database update this version of Jarvis knows about has been run.'}
            </p>
            {ctx.rules?.migrations && (
              <p className="stn-quiet" data-ledger-summary={ctx.rules.migrations.ready ? 'ready' : 'missing'}>
                {ctx.rules.migrations.ready
                  ? `The server’s ledger: ${ctx.rules.migrations.applied} migrations applied, latest ${ctx.rules.migrations.latest}${ctx.rules.migrations.pending.length ? `, ${ctx.rules.migrations.pending.length} pending` : ''}.`
                  : 'The server has no migrations ledger yet (migration 110); this list is what each page can detect for itself.'}
                {ctx.rules.migrations.ready && ctx.rules.migrations.warnings?.length > 0 && (
                  <> {ctx.rules.migrations.warnings.length} file(s) changed since they were applied.</>
                )}
              </p>
            )}
            {missing.length > 0 && <CheckList checks={missing} />}
            <details className="stn-checks-done" open={missing.length === 0}>
              <summary>{checks.length - missing.length} set up</summary>
              <CheckList checks={checks.filter((c) => c.ok)} />
            </details>
          </>
        )}
      </section>

      <section className="stn-card" id="s-recent" aria-labelledby="stn-recent-h">
        <h3 className="stn-card-title" id="stn-recent-h">Last setting changes</h3>
        <p className="stn-help">
          The most recently saved firm rules (each with its value now) and changes to feature access. Every
          feature change is also in the Audit Log.
        </p>
        {changes === null ? <p className="stn-quiet">Loading…</p> : changes.length === 0 ? (
          <p className="stn-quiet">Nothing has been changed yet: every setting is as Jarvis was installed.</p>
        ) : (
          <ol className="stn-recent">
            {changes.map((c) => (
              <li key={c.id} className="stn-recent-row">
                <span className="stn-recent-what">
                  {c.path ? <Link to={c.path}>{c.label}</Link> : c.label}
                  {c.value && <span className="stn-value">{c.value}</span>}
                </span>
                <span className="stn-recent-who">{c.by || 'Somebody'} · {ago(c.at)}</span>
              </li>
            ))}
          </ol>
        )}
      </section>

      <OpsCards me={ctx.me} />

      <AiUsageCard me={ctx.me} />

      <section className="stn-card" aria-labelledby="stn-short-h">
        <h3 className="stn-card-title" id="stn-short-h">Shortcuts</h3>
        <ul className="stn-shortcuts">
          {SHORTCUTS.map(([g, p]) => {
            const page = findPage(g, p);
            if (!page) return null;
            return (
              <li key={`${g}/${p}`}>
                <Link to={pathOf(page)} className="stn-shortcut">
                  <Icon name={page.icon} />
                  <span><strong>{page.label}</strong><small>{page.groupLabel}</small></span>
                </Link>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}

/* ===================================================== PRIVACY & MONITORING */

/**
 * Settings -> Firm & system -> Privacy & monitoring. A READ-ONLY INDEX: every
 * switch in Jarvis that records people or shows them to others, its value
 * now, and a link to where it is changed. Built from registry.js (each
 * setting flagged `privacy`), so a monitoring switch added to a page is
 * listed here by being listed there. Nothing on this page changes anything.
 */
export function PrivacyPage() {
  const ctx = useSettings();
  const rows = useMemo(() => privacyRows().map((r) => {
    let value = null;
    try { value = r.setting.value ? r.setting.value(ctx) : null; } catch { value = null; }
    return { ...r, value };
  }), [ctx]);
  const groups = [
    ['records', 'What records people', 'Locations, recordings, conversations read by an administrator, and what the device agent reports.'],
    ['exposes', 'What shows people to others', 'Who can find whom, whose photo is shown, and what is sent to Claude.']
  ];
  const live = (v) => v && !/^off$/i.test(v);
  return (
    <div className="stn-privacy">
      <p className="stn-banner is-info" role="note">
        Monitoring staff is regulated: under data-protection law people must be told what is recorded,
        why and who can see it <strong>before</strong> a switch that records them is turned on.
        This page only reads the switches — change each one where it lives. How long what they record is
        kept is under <Link to="/settings/system/retention">Retention</Link>, and why it is processed under{' '}
        <Link to="/settings/system/processing">Record of processing</Link>.
      </p>
      {groups.map(([kind, title, lead]) => (
        <section key={kind} className="stn-card" aria-labelledby={`stn-pv-${kind}`}>
          <h3 className="stn-card-title" id={`stn-pv-${kind}`}>{title}</h3>
          <p className="stn-help">{lead}</p>
          <ul className="stn-privacy-list">
            {rows.filter((r) => r.setting.privacy === kind).map((r) => (
              <li key={`${r.path}|${r.setting.label}`} className="stn-privacy-row" data-privacy={r.setting.anchor}>
                <span className="stn-privacy-what">
                  <strong>{r.setting.label}</strong>
                  <small>{r.setting.why}</small>
                </span>
                <span className={`stn-value${live(r.value) ? ' is-live' : ''}`} data-value={r.value || ''}>
                  {r.value || '—'}
                </span>
                <Link className="stn-privacy-link" to={r.path}
                      aria-label={`Change ${r.setting.label} in ${r.page.groupLabel}, ${r.page.label}`}>
                  {r.page.label}<Icon name="chevron" className="stn-chev" />
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

/* ================================================================= RECORDS */

/**
 * Settings -> Firm & system -> Records: where to look something up. Links
 * only - the pages stay under Advanced as well.
 */
const RECORDS = [
  ['/audit-log', 'Audit log', 'Every log Jarvis keeps in one list - changes, sign-ins, View as sessions, recordings opened - filtered and exported.', 'records'],
  ['/chat-archive', 'Chat archive', 'General and group conversations, opened with a reason when Conversation review allows it — and who looked, for good.', 'review'],
  ['/recordings?scope=all', 'Call recordings', 'Every recorded call and meeting, with its transcript, summary and to-dos.', 'record']
];

export function RecordsPage() {
  return (
    <ul className="stn-records">
      {RECORDS.map(([to, label, what, icon]) => (
        <li key={to}>
          <Link to={to} className="stn-record">
            <Icon name={icon} />
            <span><strong>{label}</strong><small>{what}</small></span>
            <Icon name="chevron" className="stn-chev" />
          </Link>
        </li>
      ))}
    </ul>
  );
}
